import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ReactComponent as FilterCloseIcon } from '../../../assets/icons/filter-close.svg';
import { useAppDispatch } from '../../../redux/hooks';
import { setNotificationMessage } from '../../../redux/slices/NotificationManagementSlice';
import CustomDatePicker from '../../../shared-components/custom-date-range/CustomDatePicker';
import PageTitle from '../../../shared-components/page-title/PageTitle';
import ConvertUTCtoDate from '../../../shared-functions/ConvertUTCtoDate';
import ConvertUTCtoHHMM from '../../../shared-functions/ConvertUTCtoHHMM';
import penaltyApi from '../../../utils/api/penalty-management';

interface AuditLogItem {
  time: string;
  oldValues: Record<string, unknown>;
  newValues: Record<string, unknown>;
}

interface AuditLogDateGroup {
  date: string;
  list: AuditLogItem[];
}

interface AuditLogProperties {
  currentData?: {
    id: string;
  } | null;
}

/**
 * Formats audit log values for UI rendering
 */
const formatValue = (value: unknown, fieldKey: string | undefined): string => {
  if (!value) return '-';

  // Handle durationAmountList specifically
  if (fieldKey === 'durationAmountList' && Array.isArray(value)) {
    return value
      .map((item: any) => {
        return `${item.from}–${item.to} min | ${item.mode} | ${item.amount}`;
      })
      .join(' , ');
  }

  // Handle array of primitives
  if (Array.isArray(value)) {
    return value.join(', ');
  }

  // Handle date fields
  if (typeof value === 'string' && (fieldKey === 'effectiveFrom' || fieldKey === 'effectiveTo')) {
    return ConvertUTCtoDate(value);
  }

  // Handle plain objects
  if (typeof value === 'object') {
    return JSON.stringify(value);
  }

  return String(value);
};

/**
 * Returns union of keys from old and new value objects
 */
const getMergedKeys = (
  oldValues: Record<string, unknown>,
  newValues: Record<string, unknown>,
): string[] => Array.from(new Set([...Object.keys(oldValues), ...Object.keys(newValues)]));

/* -------------------------------------------------------------------------- */
/*                               COMPONENT                                    */
/* -------------------------------------------------------------------------- */

/**
 * AuditLog
 *
 * Displays audit trail with old vs new value comparison
 */
function AuditLog({ currentData }: AuditLogProperties): JSX.Element {
  const { t } = useTranslation('penaltyManagement');
  const dispatch = useAppDispatch();

  const FIELD_LABEL_MAP: Record<string, string> = {
    dynamicPricing: t('mode'),
    ceilingType: t('ceilingType'),
    ceilingValue: t('maxCeilingValue'),
    triggerMessage: t('triggerMessage'),
    effectiveFrom: t('effectiveFrom'),
    effectiveTo: t('effectiveTo'),
    durationAmountList: t('duration'),
  };

  const [auditLogs, setAuditLogs] = useState<AuditLogDateGroup[]>([]);
  const [dateFilter, setDateFilter] = useState({
    startDate: '',
    endDate: '',
  });

  /**
   * Handles date filter changes
   */
  const handleInputChange = (name: string, value: string): void => {
    setDateFilter((previousState) => ({
      ...previousState,
      [name]: value,
    }));
  };

  /**
   * Clears date filters
   */
  const handleClearFilter = (): void => {
    setDateFilter({
      startDate: '',
      endDate: '',
    });
  };

  /**
   * Fetch audit log list
   */
  const fetchAuditLogs = async (id: string): Promise<void> => {
    try {
      const payload = {
        ...(dateFilter.startDate && { startDate: dateFilter.startDate }),
        ...(dateFilter.endDate && { endDate: dateFilter.endDate }),
      };

      console.log('Audit Log Payload:', payload); // debug

      const response = await penaltyApi.fetchAuditLogList(id, payload);

      setAuditLogs(response?.data || []);
    } catch (error: any) {
      dispatch(
        setNotificationMessage({
          message: error?.response?.data?.message || t('somethingWentWrong'),
          status: true,
          type: 'error',
        }),
      );
    }
  };

  useEffect(() => {
    if (currentData?.id) {
      fetchAuditLogs(currentData.id);
    }
  }, [currentData?.id, dateFilter.startDate, dateFilter.endDate]);

  return (
    <div className="audit-log-wrap">
      <PageTitle
        title={t('auditLog')}
        buttonLabel=""
        buttonSvgIcon={<FilterCloseIcon />}
        buttonType="none"
        onClickButton={handleClearFilter}
      />

      <div className="date-filters">
        <CustomDatePicker
          inputLabel={t('from')}
          name="startDate"
          value={dateFilter.startDate}
          onChangeValue={handleInputChange}
          placeholder={t('inputPlaceholder')}
        />
        <CustomDatePicker
          inputLabel={t('to')}
          name="endDate"
          value={dateFilter.endDate}
          onChangeValue={handleInputChange}
          placeholder={t('inputPlaceholder')}
        />
      </div>

      {auditLogs.map((group) => (
        <div key={group.date} className="audit-section">
          <div className="audit-date">{group.date}</div>

          <div className="audit-table">
            {/* Header */}
            <div className="audit-row header">
              <div>{t('time')}</div>
              <div>{t('oldValue')}</div>
              <div>{t('newValue')}</div>
            </div>

            {/* Rows */}
            {group.list.map((item) => {
              const mergedKeys = getMergedKeys(item.oldValues, item.newValues);

              return mergedKeys.map((key) => (
                <div key={`${item.time}-${key}`} className="audit-row">
                  <div className="time">{ConvertUTCtoHHMM(item.time)}</div>

                  <div className="value">
                    <span className="label">{FIELD_LABEL_MAP[key] || key}</span>
                    <span className="values">{formatValue(item.oldValues[key], key)}</span>
                  </div>

                  <div className="value">
                    <span className="label">{FIELD_LABEL_MAP[key] || key}</span>
                    <span className="values">{formatValue(item.newValues[key], key)}</span>
                  </div>
                </div>
              ));
            })}
          </div>
        </div>
      ))}
    </div>
  );
}

AuditLog.defaultProps = {
  currentData: null,
};

export default AuditLog;
